import { useState } from 'react'
import api from '../api/axios'
import { runnerApi, errorText } from '../api/migrationRunner'
import { CheckCircleIcon, ExclamationTriangleIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'

// Read-only check of one company's database (GET /companies/:id/database-check):
// which database the app really reads, whether the tools work on the same one,
// what it lacks against the template, and newer migrations recorded as applied.
// Safe on production — the only write here is the explicit "Offer them again".

interface Identity {
  hostname: string | null
  port: number | null
  serverId: number | null
  version: string | null
  database: string | null
  tables?: number
  host?: string
}

interface CheckResult {
  company: { id: string; code: string; name: string; database: string }
  appServer: { host: string | null; port: number }
  storedHost: { host: string | null; port: number | null }
  app: Identity | null
  tools: Identity | null
  sameDatabase: boolean | null
  template: { tables: number; generatedDay: number | null } | null
  missing: { tables: string[]; columns: { table: string; column: string }[] } | null
  recordedAfterTemplate: string[]
  errors: { where: string; message: string }[]
}

interface CompanyOption {
  id: string
  company_name: string
  company_code: string
  database_name: string
}

function dayLabel(day: number | null | undefined) {
  if (!day) return 'unknown date'
  const s = String(day)
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`
}

function where(id: Identity | null) {
  if (!id) return '—'
  return `${id.hostname ?? '?'}:${id.port ?? '?'} (server ${id.serverId ?? '?'}) / ${id.database ?? '?'} · ${id.tables ?? 0} tables`
}

export default function DatabaseCheckPanel({ companies }: { companies: CompanyOption[] }) {
  const [companyId, setCompanyId] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<CheckResult | null>(null)
  const [error, setError] = useState('')
  const [confirmReset, setConfirmReset] = useState(false)
  const [resetMsg, setResetMsg] = useState('')
  const [fixing, setFixing] = useState(false)
  const [fixMsg, setFixMsg] = useState<{ text: string; rowId?: number | string | null } | null>(null)

  const run = async (id = companyId) => {
    if (!id) return
    setLoading(true)
    setError('')
    setResetMsg('')
    setConfirmReset(false)
    try {
      const { data } = await api.get(`/super-admin/companies/${id}/database-check`)
      setResult(data)
    } catch (e: any) {
      setResult(null)
      setError(e.response?.data?.message || 'Check failed')
    } finally {
      setLoading(false)
    }
  }

  // Same draft + run as Database › Schema health › Fix drift (re-runnable
  // CREATE TABLE IF NOT EXISTS / ADD COLUMN IF NOT EXISTS, recorded, backed up
  // per the runner settings) — offered here because the check already knows.
  const addMissing = async () => {
    if (!result) return
    setFixing(true)
    setFixMsg(null)
    setError('')
    try {
      const fix = await runnerApi.drift(result.company.database)
      if (!fix.items.length) {
        setFixMsg({ text: 'Nothing to add — it matches the template now.' })
      } else {
        const r = await runnerApi.runDrift(result.company.database, fix.sqlHash)
        setFixMsg({ text: `Adding ${fix.items.length} item(s). Press Check again in a few seconds.`, rowId: r.rowId })
      }
    } catch (e) {
      setError(errorText(e, 'Could not add them'))
    } finally {
      setFixing(false)
    }
  }

  const resetRecords = async () => {
    try {
      const { data } = await api.post(`/super-admin/companies/${companyId}/database-check/reset-records`, { confirm: true })
      setResetMsg(
        data.reset > 0
          ? `${data.folders.length} migration(s) now show as pending on the Database page. Apply them there.`
          : 'Nothing to reset.'
      )
      await run()
    } catch (e: any) {
      setError(e.response?.data?.message || 'Reset failed')
    }
  }

  const missingCount = result?.missing ? result.missing.tables.length + result.missing.columns.length : 0

  return (
    <div className="mb-6 bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center gap-2">
        <MagnifyingGlassIcon className="w-5 h-5 text-blue-600" />
        <div>
          <h2 className="text-sm font-semibold text-slate-700">Check a company's database</h2>
          <p className="text-xs text-slate-500">
            Read-only, safe on production. Looks through the app's own connection, compares it with the template and
            with what the migration records claim.
          </p>
        </div>
      </div>
      <div className="mt-3 flex flex-col sm:flex-row gap-3">
        <select
          value={companyId}
          onChange={(e) => { setCompanyId(e.target.value); setResult(null) }}
          className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">— Select a company —</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.company_name} ({c.company_code}) — {c.database_name}
            </option>
          ))}
        </select>
        <button
          onClick={() => run()}
          disabled={!companyId || loading}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Checking…' : 'Check'}
        </button>
      </div>

      {error && <p className="mt-3 text-sm text-red-700">{error}</p>}

      {result && (
        <div className="mt-4 space-y-3 text-sm">
          <dl className="grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-x-3 gap-y-1">
            <dt className="text-slate-500">The app reads</dt>
            <dd className="font-mono text-xs break-all">{where(result.app)}</dd>
            <dt className="text-slate-500">The tools work on</dt>
            <dd className="font-mono text-xs break-all">{where(result.tools)}</dd>
            <dt className="text-slate-500">Template</dt>
            <dd>{result.template ? `${result.template.tables} tables, made ${dayLabel(result.template.generatedDay)}` : '—'}</dd>
          </dl>

          {result.sameDatabase === false && (
            <Alert tone="red">
              The app and the Super-admin tools are on <b>different databases</b>. Clean DB, Fix drift and migrations
              change the second one, which the app never reads. Fix the company's stored host before anything else.
            </Alert>
          )}
          {result.sameDatabase === true && missingCount === 0 && result.recordedAfterTemplate.length === 0 && (
            <Alert tone="green">Same database for the app and the tools, and nothing from the template is missing.</Alert>
          )}

          {result.missing && missingCount > 0 && (
            <Alert tone="amber">
              <p>
                Missing compared with the template: {result.missing.tables.length} table(s), {result.missing.columns.length} column(s).
                Adding them only creates what's missing; nothing existing is changed or dropped.
              </p>
              <ul className="mt-2 max-h-48 overflow-auto font-mono text-xs space-y-0.5">
                {result.missing.tables.map((t) => <li key={t}>table {t}</li>)}
                {result.missing.columns.map((c) => <li key={`${c.table}.${c.column}`}>{c.table}.{c.column}</li>)}
              </ul>
              <button
                onClick={addMissing}
                disabled={fixing}
                className="mt-2 px-3 py-1.5 text-xs font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50"
              >
                {fixing ? 'Starting…' : 'Add them now'}
              </button>
            </Alert>
          )}

          {result.recordedAfterTemplate.length > 0 && (
            <Alert tone="amber">
              <p>
                {result.recordedAfterTemplate.length} migration(s) newer than the template are recorded as applied here.
                If this database was rebuilt from the template (Clean DB), those records are wrong and the Database page
                won't offer them again.
              </p>
              <ul className="mt-2 max-h-40 overflow-auto font-mono text-xs space-y-0.5">
                {result.recordedAfterTemplate.map((f) => <li key={f}>{f}</li>)}
              </ul>
              {!confirmReset ? (
                <button
                  onClick={() => setConfirmReset(true)}
                  className="mt-2 px-3 py-1.5 text-xs font-medium text-amber-800 bg-white border border-amber-300 rounded-md hover:bg-amber-100"
                >
                  Offer them again…
                </button>
              ) : (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="text-xs">Only for a database rebuilt from the template. Changes records only, not the database.</span>
                  <button
                    onClick={resetRecords}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700"
                  >
                    Yes, mark them not applied
                  </button>
                  <button onClick={() => setConfirmReset(false)} className="px-3 py-1.5 text-xs text-slate-600">Cancel</button>
                </div>
              )}
            </Alert>
          )}
          {resetMsg && <p className="text-sm text-green-700">{resetMsg}</p>}
          {fixMsg && (
            <p className="text-sm text-green-700">
              {fixMsg.text}{' '}
              {fixMsg.rowId != null && (
                <a className="font-medium underline" href={`/database?run=${fixMsg.rowId}`}>Open the run</a>
              )}
            </p>
          )}

          {result.errors.length > 0 && (
            <Alert tone="red">
              {result.errors.map((e) => <p key={e.where}><b>{e.where}:</b> {e.message}</p>)}
            </Alert>
          )}
        </div>
      )}
    </div>
  )
}

function Alert({ tone, children }: { tone: 'red' | 'amber' | 'green'; children: React.ReactNode }) {
  const cls = {
    red: 'bg-red-50 border-red-200 text-red-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    green: 'bg-green-50 border-green-200 text-green-800',
  }[tone]
  const Icon = tone === 'green' ? CheckCircleIcon : ExclamationTriangleIcon
  return (
    <div className={`p-3 rounded-lg border flex items-start gap-2 ${cls}`}>
      <Icon className="w-5 h-5 flex-shrink-0" />
      <div className="min-w-0">{children}</div>
    </div>
  )
}
